import { tagged } from "@mirage/service-logging";
import { namespace } from "@mirage/service-operational-metrics";
import { SENTRY_IGNORE_ERRORS } from "@mirage/shared/sentry";

const metrics = namespace("crash_free_rate");
const logger = tagged("crash-free-rate");
const sent = new Set<string>();

export type TRACKED_APPS = "hornet" | "web" | "scout";
export type VALID_PROCESSES =
  | "main"
  | "worker"
  | "renderer"
  | "renderer-launcher"
  | "web"
  | "background"
  | "popup";

/**
 * Called when the session is started. Should be called from the session
 * manager callbacks for each app
 */
export function sessionStarted(
  app: TRACKED_APPS,
  process: VALID_PROCESSES,
  workerName?: string,
) {
  sent.clear();

  metrics.counter("session_start", 1, {
    app,
    process,
    ...(workerName ? { workerName } : {}),
  });
}

/**
 * Called when the session is finished.
 */
export function sessionFinished(app: TRACKED_APPS, process: VALID_PROCESSES) {
  metrics.counter("session_finished", 1, { app, process });
}

/**
 * Records a crash event for the specified app and process.
 * It will record if this is the first crash this session or not so we can
 * accurately calculate our crash-free rate.
 * Crashes that match patterns in SENTRY_IGNORE_ERRORS will not be recorded.
 */
export function recordCrash(
  app: TRACKED_APPS,
  process: VALID_PROCESSES,
  error: Error | undefined,
  workerName?: string,
) {
  // Check if this error should be ignored
  if (shouldIgnoreError(error)) {
    logger.info("Error ignored due to matching ignore pattern", {
      app,
      process,
      workerName,
      error: error,
    });
    return;
  }

  logger.error("Crash recorded", {
    app,
    process,
    workerName,
    error: error,
  });

  const key = `${app}:${process}:${workerName}`;
  if (!sent.has(key)) {
    metrics.counter("crash", 1, {
      app,
      process,
      firstCrashThisSession: true,
      ...(workerName ? { workerName } : {}),
    });
    sent.add(key);
  } else {
    metrics.counter("crash", 1, {
      app,
      process,
      firstCrashThisSession: false,
      ...(workerName ? { workerName } : {}),
    });
  }
}

/**
 * Checks if an error should be ignored based on the SENTRY_IGNORE_ERRORS patterns.
 * This function iterates through all categories in SENTRY_IGNORE_ERRORS and checks
 * if the error message matches any of the patterns (regex or exact string match).
 */
function shouldIgnoreError(error: Error | undefined): boolean {
  if (!error) {
    return false;
  }

  const errorMessage = error.message;
  const errorName = error.name;

  // Check against all categories in SENTRY_IGNORE_ERRORS
  for (const category of Object.values(SENTRY_IGNORE_ERRORS)) {
    for (const pattern of category) {
      if (pattern instanceof RegExp) {
        // Test both error message and error name against regex patterns
        if (pattern.test(errorMessage) || pattern.test(errorName)) {
          return true;
        }
      } else {
        // For string patterns, check for substring match
        if (errorMessage?.includes(pattern) || errorName?.includes(pattern)) {
          return true;
        }
      }
    }
  }

  return false;
}
